<template>
  <div v-if="show" class="ap-overlay" @click.self="$emit('close')">
    <div class="ap-modal">
      <div class="ap-scrollbar-cap is-top"></div>
      <div class="ap-scrollbar-cap is-bottom"></div>
      <div class="ap-modal-scroll">
        <div class="ap-modal-head">
          <h5 class="ap-modal-title">Client Details</h5>
          <button class="ap-modal-close" @click="$emit('close')">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div v-if="client">

          <!-- ── Section 1: Overview ─────────────────────────────── -->
          <div class="ap-detail-grid">
            <div class="cdm-section-label">Overview</div>
            <div class="ap-detail-row">
              <span class="ap-detail-label">Client</span>
              <span class="ap-detail-val">{{ client.client || '-' }}</span>
            </div>
            <div v-if="client.initial" class="ap-detail-row">
              <span class="ap-detail-label">Initial</span>
              <span class="ap-detail-val">{{ client.initial }}</span>
            </div>
            <div v-if="displayClientType" class="ap-detail-row">
              <span class="ap-detail-label">Business Type</span>
              <span class="ap-detail-val">{{ displayClientType }}</span>
            </div>
            <div v-if="displayServices && displayServices.length" class="ap-detail-row">
              <span class="ap-detail-label">Service Type</span>
              <span class="ap-detail-val cdm-svc-badges">
                <app-badge
                  v-for="svc in displayServices"
                  :key="svc.name"
                  :services="svc.name"
                  size="md"
                  :caps="true"
                >{{ svc.display_name }}</app-badge>
              </span>
            </div>
            <div class="ap-detail-row">
              <span class="ap-detail-label">Status</span>
              <span class="ap-detail-val">
                <app-badge>{{ client.status || 'Pending' }}</app-badge>
                <!-- <span class="ap-status-badge" :class="'ap-status-' + (client.status || 'pending').toLowerCase()">
                  {{ client.status || 'Pending' }}
                </span> -->
              </span>
            </div>
          </div>

          <!-- ── Section 2: Company Details ─────────────────────── -->
          <template v-if="hasCompanyFields">
            <div class="ap-detail-grid">
              <div class="cdm-section-label">Company Details</div>
              <div v-if="displaySsmNew" class="ap-detail-row">
                <span class="ap-detail-label">SSM (New)</span>
                <span class="ap-detail-val ap-mono">{{ displaySsmNew }}</span>
              </div>
              <div v-if="displaySsmOld" class="ap-detail-row">
                <span class="ap-detail-label">SSM (Old)</span>
                <span class="ap-detail-val ap-mono">{{ displaySsmOld }}</span>
              </div>
              <div v-if="displayTaxNumber" class="ap-detail-row">
                <span class="ap-detail-label">Tax No.</span>
                <span class="ap-detail-val ap-mono">{{ displayTaxNumber }}</span>
              </div>
              <div v-if="displayENumber" class="ap-detail-row">
                <span class="ap-detail-label">E Number</span>
                <span class="ap-detail-val ap-mono">{{ displayENumber }}</span>
              </div>
              <div v-if="displayIrbBranch" class="ap-detail-row">
                <span class="ap-detail-label">IRB Branch</span>
                <span class="ap-detail-val">{{ displayIrbBranch }}</span>
              </div>
              <div v-if="displayAuditor" class="ap-detail-row">
                <span class="ap-detail-label">Auditor</span>
                <span class="ap-detail-val">{{ displayAuditor }}</span>
              </div>
              <div v-if="displayYearEnd" class="ap-detail-row">
                <span class="ap-detail-label">Year End</span>
                <span class="ap-detail-val">{{ displayYearEnd }}</span>
              </div>
            </div>
          </template>

          <!-- ── Section 3: Contacts ─────────────────────────────── -->
          <template v-if="sortedContacts.length">
            <div class="cdm-contact-list">
              <div class="cdm-section-label">Contacts</div>
              <div class="cdm-contact-card-wrapper">
                <div
                  v-for="(contact, idx) in sortedContacts"
                  :key="idx"
                  class="cdm-contact-card"
                  :class="{ 'is-pic': isPic(contact) }"
                >
                  <div class="cdm-contact-head">
                    <div class="cdm-contact-identity">
                      <span class="cdm-contact-name">
                        {{ [contact.title, contact.name].filter(Boolean).join(' ') || '—' }}
                      </span>
                      <span v-if="contact.label" class="cdm-contact-category">{{ contact.label }}</span>
                    </div>
                    <div v-if="contact.roles && contact.roles.length" class="cdm-contact-roles">
                      <span
                        v-for="role in contact.roles"
                        :key="role"
                        class="cdm-role-chip"
                        :class="'cdm-role-' + role"
                      >{{ role.toUpperCase() }}</span>
                    </div>
                  </div>
                  <div v-if="contact.phone || contact.email" class="cdm-contact-body">
                    <div v-if="contact.phone" class="ap-detail-row">
                      <span class="ap-detail-label">Phone</span>
                      <span class="ap-detail-val ap-mono">{{ contact.phone }}</span>
                    </div>
                    <div v-if="contact.email" class="ap-detail-row">
                      <span class="ap-detail-label">Email</span>
                      <span class="ap-detail-val">{{ contact.email }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

        </div>

        <!-- ── Documents ──────────────────────────────────────────── -->
        <template v-if="showDocuments">
          <div class="ap-doc-section">
            <div class="ap-doc-head">
              <span>Documents</span>
              <span v-if="documents.length" class="ap-doc-badge">{{ documents.length }}</span>
            </div>
            <div v-if="docsLoading" class="ap-doc-loading">
              <fa-icon :icon="['fas', 'spinner']" spin /> Loading…
            </div>
            <empty-state
              v-else-if="!documents.length"
              :icon="['fas', 'file']"
              icon-bg="#f3f4f6"
              icon-color="#9ca3af"
              title="No documents"
              message="No documents uploaded yet."
            />
            <div v-else class="ap-doc-list">
              <div v-for="doc in documents" :key="doc.id" class="ap-doc-item">
                <div class="ap-doc-icon" :class="'ap-doc-' + doc.file_type">
                  <fa-icon :icon="docFileIcon(doc.original_name)" />
                </div>
                <div class="ap-doc-info">
                  <span class="ap-doc-name">{{ doc.original_name }}</span>
                  <span class="ap-doc-meta">{{ docFormatBytes(doc.file_size) }} · {{ formatDate(doc.uploaded_at) }} · <em>{{ doc.section }}</em></span>
                </div>
                <button class="ap-row-action" title="Download" @click="$emit('download-doc', doc)">
                  <fa-icon :icon="['fas', 'download']" />
                </button>
                <button class="ap-row-action ap-row-action-danger" title="Delete" @click="$emit('delete-doc', doc.id)">
                  <fa-icon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
          </div>
        </template>

        <button class="ap-btn ap-btn-ghost ap-btn-block" style="margin-top: 20px" @click="$emit('close')">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import EmptyState from '@/components/shared/EmptyState.vue'
import AppBadge from '@/components/shared/AppBadge.vue'
import bodyScrollLockMixin from '@/mixins/bodyScrollLockMixin'

export default {
  name: 'client-details-modal',
  mixins: [bodyScrollLockMixin],
  components: { EmptyState, AppBadge },
  props: {
    show: { type: Boolean, default: false },
    client: { type: Object, default: null },
    documents: { type: Array, default: () => [] },
    docsLoading: { type: Boolean, default: false },
    showDocuments: { type: Boolean, default: true }
  },
  emits: ['close', 'download-doc', 'delete-doc'],
  computed: {
    ci () { return this.client && this.client.company_identity },

    // ── Section 1 ────────────────────────────────────────────────
    displayClientType () {
      const t = (this.client && this.client.client_type) || (this.ci && this.ci.client_type)
      const map = { sdn_bhd: 'Sdn Bhd', llp: 'LLP', sole_prop: 'Sole Prop', partnership: 'Partnership', salaried_person: 'Salaried' }
      return map[t] || t || null
    },
    displayServices () {
      const cat = this.ci && this.ci.category
      if (!cat || !Array.isArray(cat) || !cat.length) return []
      return cat
        .filter(c => c.name)
        .map(c => ({ name: c.name, display_name: c.display_name || c.name }))
    },

    // ── Section 2 ────────────────────────────────────────────────
    displayYearEnd () {
      const ye = (this.client && this.client.year_end) || (this.ci && this.ci.year_end)
      if (!ye) return null
      const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return M[ye - 1] || null
    },
    displaySsmNew () {
      return (this.client && this.client.ssm_number_new) || (this.ci && this.ci.ssm_number_new) || null
    },
    displaySsmOld () {
      return (this.client && this.client.ssm_number_old) || (this.ci && this.ci.ssm_number_old) || null
    },
    displayTaxNumber () {
      const tn = (this.client && this.client.tax_number) || (this.ci && this.ci.tax_number)
      if (!tn) return null
      if (typeof tn === 'object') {
        const val = (tn.group || '') + (tn.number || '')
        return val || null
      }
      return tn || null
    },
    displayENumber () {
      const en = (this.client && this.client.e_number) || (this.ci && this.ci.e_number)
      if (!en) return null
      if (typeof en === 'object') {
        const val = (en.prefixed || '') + (en.number || '')
        return val || null
      }
      return en || null
    },
    displayIrbBranch () {
      return (this.client && this.client.irb_branch) || (this.ci && this.ci.irb_branch) || null
    },
    displayAuditor () {
      return (this.client && this.client.auditor) || (this.ci && this.ci.auditor) || null
    },
    hasCompanyFields () {
      return !!(this.displaySsmNew || this.displaySsmOld || this.displayTaxNumber ||
        this.displayENumber || this.displayIrbBranch || this.displayAuditor || this.displayYearEnd)
    },

    // ── Section 3 ────────────────────────────────────────────────
    sortedContacts () {
      const contacts = (this.client && this.client.contacts) || []
      if (!Array.isArray(contacts) || !contacts.length) return []
      const pics = contacts.filter(c => this.isPic(c))
      const others = contacts.filter(c => !this.isPic(c))
      return [...pics, ...others]
    }
  },
  methods: {
    isPic (contact) {
      return Array.isArray(contact.roles) && contact.roles.includes('pic')
    },
    docFileIcon (filename) {
      const ext = (filename || '').split('.').pop().toLowerCase()
      if (ext === 'pdf') return ['fas', 'file-pdf']
      if (['jpg', 'jpeg', 'png'].includes(ext)) return ['fas', 'file-image']
      if (ext === 'csv') return ['fas', 'file-csv']
      return ['fas', 'file']
    },
    docFormatBytes (bytes) {
      if (!bytes) return '0 B'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    formatDate (str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d)) return str
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
  }
}
</script>

<style scoped>

</style>
